import React, { ReactNode, useState } from 'react';
import {
  Box,
  Table,
  TableColumnType,
  RowKeyAccessor,
  LazyLoadList,
  LazyLoadListScrollTrigger,
  Flex,
  Spinner,
} from '@fullstory/components';
import { useBulkActions, BulkAction } from './bulkActions';
import { RowAction, useRowActionColumn } from './rowActions';
import css from './index.module.scss';
import { BulkActionResourceType } from './types';

interface BulkActionTableProps<R>
  extends Omit<
    React.ComponentProps<typeof Table>,
    'rows' | 'columns' | 'bodyProps' | 'rowKeyAccessor'
  > {
  /** Pass through key for the purposes of container identity (useful for resetting the component on filter) */
  containerKey?: string | number;
  /** From the Table props, but we have to explicitly type this so that we can use the generic. */
  rowKeyAccessor: RowKeyAccessor<R>;
  /**
   * The type of resources rendered in the table ('Property', 'Page', etc).
   * Used to render translated messages in the table.
   * */
  resourceType: BulkActionResourceType;
  /** Callback when a row is clicked. */
  onRowClick?: (row: R) => void;
  /**
   * Set of bulk actions to display when items are selected.
   * See the provided bulk action hooks like useButtonAction.
   */
  bulkActions: BulkAction<R>[];
  /**
   * Set of actions to display in a menu for each row.
   */
  rowActions: RowAction<R>[];
  /**
   * Set of filters to display above the table.
   * See the provided filter hooks like useSearchFilter.
   */
  filters: ReactNode | ((resetSelected: () => void) => ReactNode);
  /** Set of rows to display */
  rows: R[];
  /**
   * Set of columns to display.
   * The table will add a column of checkboxes to the head
   * and a column of row action menus to the tail of this list
   */
  columns: TableColumnType<R>[];
  /**
   * Callback that defines whether a row is selectable.
   * If false, the checkbox cell will not be rendered.
   * All rows are selectable if the prop is omitted.
   */
  isSelectable?: (row: R) => boolean;
  /**
   * Override the count of rows in the bulk actions element.
   * In some situations the number of rows in the table does not
   * accurately convey the total number of elements,
   * so this prop lets us explicitly set the count.
   */
  getCount?: (row: R[]) => number;
  /**
   * When provided, the callback is invoked when the end of the table is reached.
   * It is on the caller to load more rows and pass them along in the rows prop.
   */
  onLoadMore?: () => void;
  /**
   * When provided, the callback is invoked when the num items to render changes, including the initial render.
   * This callback is only applicable when the bulk action table is able to manage lazy loading (i.e. onLoadMore is not in use).
   */
  onRenderPage?: (numItemsToRender: number) => void;
  /**
   * When provided, will render additional header content above the table.
   */
  additionalHeaderContent?: ReactNode;
  /**
   * When provided, will render the new simplified bulk actions UI.
   * TODO: once we have fully committed to this new design, swap out other bulk actions UI.
   */
  simplified?: boolean;
  /**
   * When provided, will trigger a spinner element to spin until isLoading is false
   */
  isLoading?: boolean;
  testId?: string;
  isPaginated?: boolean;

  /**
   * When set, hide the overflowY scroll property
   */
  ignoreScroll?: boolean;

  /**
   * When set, will set the min height of the table before scrolling. Defaults to 100
   */
  minTableHeight?: number;
  /**
   * When set, will set the max height of the table before scrolling. Defaults to 600
   */
  maxTableHeight?: number;
  /**
   * When set, disables the "Select All" button that appears when all rows on the current page are selected
   */
  disableSelectAll?: boolean;
}

/**
 * Table that allows users to perform actions on a selected set of rows.
 * @param R The row type. Row items must have a string ID.
 */
export function BulkActionTable<R>({
  containerKey,
  resourceType,
  onRowClick,
  bulkActions,
  rowActions,
  filters,
  rows,
  columns,
  isSelectable,
  getCount,
  onLoadMore,
  onRenderPage,
  additionalHeaderContent,
  testId = 'bulk-actions',
  rowKeyAccessor,
  isPaginated,
  simplified,
  isLoading,
  ignoreScroll = false,
  minTableHeight = 100,
  maxTableHeight = 600,
  disableSelectAll,
  ...props
}: BulkActionTableProps<R>) {
  const [overflowContainerRef, setOverflowContainerRef] = useState<HTMLDivElement | null>(null);

  const { selectedColumn, bulkActionsElement, resetSelected } = useBulkActions(
    resourceType,
    testId,
    rows,
    rowKeyAccessor,
    bulkActions,
    getCount ? getCount(rows) : undefined,
    isSelectable,
    props.fixed,
    simplified || false,
    isPaginated || false,
    disableSelectAll,
  );

  const rowActionColumn = useRowActionColumn<R>(
    rowActions,
    testId,
    props.fixed || false,
    resetSelected,
  );

  const augmentedColumns = React.useMemo(() => {
    const cols = [selectedColumn, ...columns];
    if (rowActionColumn) {
      cols.push(rowActionColumn);
    }
    return cols;
  }, [selectedColumn, columns, rowActionColumn]);

  const bodyProps = onRowClick ? { rowProps: { onClick: onRowClick } } : undefined;

  return (
    <Box data-component="BulkActionTable" className={css.table}>
      <Box padding={[0, 24]}>
        {typeof filters === 'function' ? filters(resetSelected) : filters}
        <Flex justifyContent="space-between">
          {bulkActionsElement}
          {additionalHeaderContent}
        </Flex>
      </Box>
      <Box style={{ position: 'relative' }}>
        <Box
          key={containerKey}
          minHeight={minTableHeight}
          maxHeight={maxTableHeight}
          overflowY={ignoreScroll ? undefined : 'scroll'}
          ref={setOverflowContainerRef}
          style={{ opacity: isLoading ? 0.5 : 1 }}
        >
          {onLoadMore ? (
            <>
              <Table
                rowKeyAccessor={rowKeyAccessor}
                rows={rows}
                columns={augmentedColumns}
                bodyProps={bodyProps}
                headProps={{
                  variant: 'dark',
                  rowProps: { cellProps: { fixed: true, className: css.tableHeader } },
                }}
                {...props}
              />
              <LazyLoadListScrollTrigger
                rootRef={overflowContainerRef}
                renderMore={onLoadMore}
                key={rows.length}
              />
            </>
          ) : (
            <LazyLoadList
              numOfItems={rows.length}
              active={rows.length > 40}
              initialPageSize={40}
              rootRef={overflowContainerRef}
              onRenderPage={onRenderPage}
            >
              {({ shouldRenderIndex }) => (
                <Table
                  rowKeyAccessor={rowKeyAccessor}
                  rows={rows.filter((_, i) => shouldRenderIndex(i))}
                  columns={augmentedColumns}
                  bodyProps={bodyProps}
                  headProps={{
                    variant: 'dark',
                    rowProps: { cellProps: { fixed: true, className: css.tableHeader } },
                  }}
                  {...props}
                />
              )}
            </LazyLoadList>
          )}
        </Box>
        {isLoading && (
          <Flex
            justifyContent="center"
            alignItems="center"
            // overlays the table
            // adjust top a bit to account for the table header
            style={{
              position: 'absolute',
              top: 40,
              right: 0,
              bottom: 0,
              left: 0,
              opacity: 1,
              zIndex: 1000,
            }}
          >
            <Spinner diameter={48} />
          </Flex>
        )}
      </Box>
    </Box>
  );
}
